import {
  TradeItem,
  Counterparty,
  PaginatedTradeList,
  TradeStatus,
  TradeType,
  FrameworkAgreement,
  CollateralType,
  SettlementVenue,
  BenchmarkRate,
  DeliveryType,
  InventoryItem,
  PositionListItem,
  TermType,
} from '@/types';
import { getAgreementType } from '@/types';
import { createMockInventoryItem } from './inventory-fixtures';
import { dayCountForCurrency } from '@/services/trades/trade-calculations';

export const createMockTradeItem = (overrides?: Partial<TradeItem>): TradeItem => ({
  id: 1,
  status: 'active',
  status_display: 'Active',
  symbol_id: 'AAPL.NASDAQ',
  description: 'Apple Inc',
  isin: 'US0378331005',
  counterparty: 'Counterparty A',
  counterpartyId: 1,
  agreement: 'gmsla',
  agreement_display: 'GMSLA',
  trade_type: 'lend',
  trade_type_display: 'Lend',
  collateral_type: 'cash',
  quantity: 1000,
  gross_price: 150.25,
  gross_price_raw: '150.25',
  cob_price: null,
  cob_price_raw: null,
  // LR-494: COB FX rate twin (security → settlement); null by default. Cross-ccy
  // tests override it; same-ccy rows ignore it (column shows "—").
  cob_fx_rate: null,
  // LR-521: COB margin price + gross/margin values (+ settlement-ccy twins);
  // null by default (no cob_price) → COB value columns show "—". Tests override.
  cob_margin_price: null,
  cob_margin_price_converted: null,
  cob_gross_value: null,
  cob_gross_value_converted: null,
  cob_margin_value: null,
  cob_margin_value_converted: null,
  current_market_price: null,
  current_market_price_updated_at: null,
  margin_price: null,
  contract_value: 150250.0,
  margin: 105, // margin PERCENTAGE (105 = 105%), not a dollar amount — see computeMtmRow
  total_rate: null,
  fee_percent: null,
  daily_earnings: 41.11,
  trade_date: '2024-01-01',
  settlement_date: '2024-01-02',
  maturity_date: '',
  custodian_name: null,
  currency: 'USD',
  // LR-474: settlement fields present like the real payload (settlement_currency
  // is required on the backend). Defaults model a SAME-CCY trade: settlement ccy
  // follows the (possibly overridden) security currency, fx 1, no converted
  // twins. Cross-ccy tests override settlement_currency/fx_rate/*_converted
  // explicitly.
  settlement_currency: overrides?.currency ?? 'USD',
  fx_rate: 1,
  margin_price_converted: null,
  contract_value_converted: null,
  daily_earnings_converted: null,
  closing_cash_converted: null,
  // LR-476/522: marked (post-MtM) display twins + Last Marked / Trade gross
  // values. Default to null — matching the backend's null-when-never-marked
  // semantics (LR-522 dropped the LR-476 booking fallback). Tests exercising a
  // real mark or a specific value override these explicitly (or go through the
  // blotter pipeline's withMarkedFields).
  marked_margin_price: null,
  marked_contract_value: null,
  marked_margin_price_converted: null,
  marked_contract_value_converted: null,
  trade_gross_value: null,
  trade_gross_value_converted: null,
  last_marked_gross_value: null,
  last_marked_gross_value_converted: null,
  created_at: '2024-01-01T10:30:00Z',
  updated_at: '2024-01-01T10:30:00Z',
  created_by: 'user@example.com',
  ...overrides,
});

export const createMockPendingOpenTradeItem = (
  overrides?: Partial<TradeItem>,
): TradeItem =>
  createMockTradeItem({
    id: 901,
    status: 'pending_open_settlement',
    status_display: 'Pending Settlement',
    ...overrides,
  });

export const createMockTradeList = (count: number = 10): TradeItem[] => {
  const statuses: TradeStatus[] = ['active', 'pending', 'closed', 'cancelled'];
  const tradeTypes: TradeType[] = [
    'lend',
    'borrow',
    'repo',
    'reverse_repo',
  ];
  const collateralTypes: CollateralType[] = ['cash', 'non_cash', 'uncollateralized'];
  const counterparties = ['Counterparty A', 'Counterparty B', 'Counterparty C', 'Counterparty D'];
  const securities = ['AAPL.NASDAQ', 'MSFT.NASDAQ', 'GOOGL.NASDAQ', 'AMZN.NASDAQ', 'TSLA.NASDAQ'];
  const isins = ['US0378331005', 'US5949181045', 'US02079K3059', 'US0231351067', 'US88160R1014'];
  const currencies = ['USD', 'EUR', 'GBP', 'JPY'];
  return Array.from({ length: count }, (_, index) => {
    const baseDate = new Date('2024-01-01');
    baseDate.setDate(baseDate.getDate() + index);
    const tradeDate = baseDate.toISOString().split('T')[0];
    const settlementDate = new Date(baseDate);
    settlementDate.setDate(settlementDate.getDate() + 1);
    const settlementDateStr = settlementDate.toISOString().split('T')[0];
    const securityIndex = index % securities.length;
    const quantity = (index + 1) * 1000;
    const price = 100 + index * 10;
    return createMockTradeItem({
      id: index + 1,
      status: statuses[index % statuses.length],
      status_display: statuses[index % statuses.length].charAt(0).toUpperCase() + statuses[index % statuses.length].slice(1),
      symbol_id: securities[securityIndex],
      description: `Description for ${securities[securityIndex]}`,
      isin: isins[securityIndex],
      counterparty: counterparties[index % counterparties.length],
      // The pair is derived, never cycled independently: the API rejects a
      // mismatch (Position.clean), and half of every generated list used to be
      // an impossible row — repo labelled GMSLA, borrow labelled GMRA.
      agreement: getAgreementType(tradeTypes[index % tradeTypes.length]),
      agreement_display: getAgreementType(tradeTypes[index % tradeTypes.length]).toUpperCase(),
      trade_type: tradeTypes[index % tradeTypes.length],
      trade_type_display: tradeTypes[index % tradeTypes.length],
      collateral_type: collateralTypes[index % collateralTypes.length],
      quantity,
      gross_price: price,
      // ~60% of generated trades carry a marked price: a small +/- move off gross.
      last_marked_price: index % 5 === 0 || index % 5 === 1 ? null : +(price * (1 + ((index % 7) - 3) * 0.01)).toFixed(2),
      contract_value: quantity * price,
      // margin is a PERCENTAGE (e.g. 105 = 105%), not a dollar amount — mirrors createMockTradeItem default of 105.
      margin: 100 + (index % 10), // 100..109 percent
      daily_earnings: quantity * price * 0.001,
      // Every fourth trade is a fee trade, so the fee and cost legs have figures
      // under `pnpm mock` (they are blank on a rebate trade).
      rate_entry_mode: index % 4 === 0 ? 'fee' : 'rebate',
      fee_income: +(quantity * price * 0.00006).toFixed(4),
      cost_of_collateral: +(quantity * price * 0.00014).toFixed(4),
      net_amount: quantity * price * (index % 4 === 0 ? -0.00008 : 0.001),
      trade_date: tradeDate,
      settlement_date: settlementDateStr,
      maturity_date: index % 3 === 0 ? '' : (() => {
        const maturity = new Date(settlementDate);
        maturity.setDate(maturity.getDate() + 30);
        return maturity.toISOString().split('T')[0];
      })(),
      currency: currencies[index % currencies.length],
      created_by: `user${index + 1}@example.com`,
      created_at: `${tradeDate}T${String(9 + (index % 8)).padStart(2, '0')}:${String((index % 60)).padStart(2, '0')}:00Z`,
      updated_at: `${tradeDate}T${String(9 + (index % 8)).padStart(2, '0')}:${String((index % 60)).padStart(2, '0')}:00Z`,
    });
  });
};

const convertTradeItemToPositionListItem = (trade: TradeItem): PositionListItem => ({
  id: trade.id,
  status: trade.status,
  status_display: trade.status_display,
  symbol_id: trade.symbol_id,
  description: trade.description,
  isin: trade.isin,
  currency: trade.currency,
  trade_type: trade.trade_type,
  trade_type_display: trade.trade_type_display,
  agreement: trade.agreement,
  agreement_display: trade.agreement_display,
  counterparty_name: trade.counterparty,
  counterparty_id: trade.counterpartyId,
  custodian_name: trade.custodian_name,
  quantity: trade.quantity,
  // LR-388: forward the pending recall + remaining shape so MSW round-trips them.
  pending_recall_quantity: trade.pending_recall_quantity,
  remaining_quantity: trade.remaining_quantity,
  // LR-497: forward the settlement-leg + BO-retry fields so the round-trip
  // preserves the inputs getEffectiveTradeStatus / the retry chip read. Without
  // these the converter silently strips them, so a mock FOP one-leg row collapses
  // back to plain "Pending Settlement" and the retry counter never renders.
  delivery_type: trade.delivery_type,
  cash_settled: trade.cash_settled,
  securities_settled: trade.securities_settled,
  cash_settled_at: trade.cash_settled_at,
  securities_settled_at: trade.securities_settled_at,
  bo_retry_count: trade.bo_retry_count,
  last_bo_error: trade.last_bo_error,
  // LR-711: the stuck-posting row signal reads this after the list round-trip.
  // Dropped here, the signal would be green in unit tests and dead under
  // `pnpm mock` — the same trap the comment above `delivery_type` describes.
  bo_posting: trade.bo_posting ?? null,
  gross_price: trade.gross_price.toString(),
  // LR-521: forward COB (T-1) price through the list round-trip too (only the
  // detail builder did before), so "COB Gross Price" renders under `pnpm mock`
  // alongside its derived COB margin price / values.
  cob_price: trade.cob_price_raw ?? (trade.cob_price != null ? trade.cob_price.toString() : null),
  current_market_price: trade.current_market_price !== null ? trade.current_market_price.toString() : null,
  current_market_price_updated_at: trade.current_market_price_updated_at ?? null,
  margin_price: trade.margin_price !== null ? trade.margin_price.toString() : null,
  contract_value: trade.contract_value.toString(),
  margin: trade.margin.toString(),
  daily_earnings: trade.daily_earnings !== null ? trade.daily_earnings.toString() : '0',
  trade_date: trade.trade_date,
  settlement_date: trade.settlement_date,
  maturity_date: trade.maturity_date,
  created_at: trade.created_at,
  updated_at: trade.updated_at,
  // Forward mark fields from the TradeItem's *_raw camelCase mirrors back to the API
  // shape. transformPositionToTradeItem on the client re-derives the *_raw fields, so
  // dropping them here would erase the marks state from the round-trip mock data.
  last_marked_price: trade.last_marked_price_raw ?? null,
  latest_mark_price: trade.latest_mark_price_raw ?? null,
  // LR-474: forward the settlement family — the round-trip used to strip these,
  // silently turning every mock cross-ccy trade back into a same-ccy one.
  settlement_currency: trade.settlement_currency,
  fx_rate: trade.fx_rate != null ? trade.fx_rate.toFixed(6) : undefined,
  // LR-494: COB (T-1 close) FX rate twin — forward so the round-trip preserves it.
  cob_fx_rate: trade.cob_fx_rate != null ? trade.cob_fx_rate.toFixed(6) : null,
  // LR-521: COB margin price + gross/margin values (+ settlement-ccy twins).
  cob_margin_price: trade.cob_margin_price != null ? trade.cob_margin_price.toString() : null,
  cob_margin_price_converted:
    trade.cob_margin_price_converted != null ? trade.cob_margin_price_converted.toString() : null,
  cob_gross_value: trade.cob_gross_value != null ? trade.cob_gross_value.toString() : null,
  cob_gross_value_converted:
    trade.cob_gross_value_converted != null ? trade.cob_gross_value_converted.toString() : null,
  cob_margin_value: trade.cob_margin_value != null ? trade.cob_margin_value.toString() : null,
  cob_margin_value_converted:
    trade.cob_margin_value_converted != null ? trade.cob_margin_value_converted.toString() : null,
  margin_price_converted:
    trade.margin_price_converted != null ? trade.margin_price_converted.toString() : null,
  contract_value_converted:
    trade.contract_value_converted != null ? trade.contract_value_converted.toString() : null,
  daily_earnings_converted:
    trade.daily_earnings_converted != null ? trade.daily_earnings_converted.toString() : null,
  // LR-626: forward the NATIVE closing cash and its projected flag too, not just
  // the settlement twin — the list round-trip used to strip them, so a same-ccy
  // GMRA row (no twin) rendered '-' while only the cross-ccy one showed a figure.
  closing_cash: trade.closing_cash != null ? trade.closing_cash.toString() : null,
  closing_cash_projected: trade.closing_cash_projected ?? false,
  // The Fee Income / Cash Pool Interest Accrual / Net Amount cells and their gate.
  rate_entry_mode: trade.rate_entry_mode,
  fee_income: trade.fee_income != null ? trade.fee_income.toString() : null,
  cost_of_collateral: trade.cost_of_collateral != null ? trade.cost_of_collateral.toString() : null,
  effective_cost_of_collateral:
    trade.effective_cost_of_collateral != null ? trade.effective_cost_of_collateral.toString() : null,
  net_amount: trade.net_amount != null ? trade.net_amount.toString() : null,
  effective_net_amount:
    trade.effective_net_amount != null ? trade.effective_net_amount.toString() : null,
  closing_cash_converted:
    trade.closing_cash_converted != null ? trade.closing_cash_converted.toString() : null,
  // LR-522: Trade Gross Value (+ settlement twin).
  trade_gross_value: trade.trade_gross_value != null ? trade.trade_gross_value.toString() : null,
  trade_gross_value_converted:
    trade.trade_gross_value_converted != null ? trade.trade_gross_value_converted.toString() : null,
  // LR-476/522: forward the Last Marked display twins — the round-trip used to
  // strip these, so under `pnpm mock` the Last Marked columns rendered '-'.
  // transformPositionToTradeItem parses them back to numbers on the client.
  marked_margin_price: trade.marked_margin_price != null ? trade.marked_margin_price.toString() : null,
  marked_margin_price_converted:
    trade.marked_margin_price_converted != null ? trade.marked_margin_price_converted.toString() : null,
  marked_contract_value:
    trade.marked_contract_value != null ? trade.marked_contract_value.toString() : null,
  collateral_to_return:
    trade.collateral_to_return != null ? trade.collateral_to_return.toString() : null,
  marked_contract_value_converted:
    trade.marked_contract_value_converted != null ? trade.marked_contract_value_converted.toString() : null,
  // LR-522: Last Marked Gross Value (+ settlement twin).
  last_marked_gross_value:
    trade.last_marked_gross_value != null ? trade.last_marked_gross_value.toString() : null,
  last_marked_gross_value_converted:
    trade.last_marked_gross_value_converted != null ? trade.last_marked_gross_value_converted.toString() : null,
});

export const createMockPaginatedTradeList = (
  count: number = 10,
  totalCount: number = 100
): PaginatedTradeList => ({
  count: totalCount,
  next: count < totalCount ? 'http://api.example.com/trades?page=2' : null,
  previous: null,
  results: createMockTradeList(count).map(convertTradeItemToPositionListItem),
});

export const createMockPaginatedTradeListFromBlotter = (
  limit: number = 20,
  offset: number = 0
): PaginatedTradeList => {
  const totalCount = mockTradeBlotterData.length;
  const startIndex = offset;
  const endIndex = Math.min(startIndex + limit, totalCount);
  const results = mockTradeBlotterData.slice(startIndex, endIndex).map(convertTradeItemToPositionListItem);
  return {
    count: totalCount,
    next: endIndex < totalCount ? `http://api.example.com/trades?limit=${limit}&offset=${endIndex}` : null,
    previous: startIndex > 0 ? `http://api.example.com/trades?limit=${limit}&offset=${Math.max(0, startIndex - limit)}` : null,
    results,
  };
};

export const createMockCounterparty = (overrides?: Partial<Counterparty>): Counterparty => ({
  id: 1,
  name: 'Counterparty A',
  clientId: '1234567890',
  legalEntity: 'International',
  agreements: [
    {
      id: 'agr-1',
      type: 'gmsla',
      currency: 'USD',
      agreementId: 'AGR-1001',
      validFrom: '2023-01-01',
      isPerpetual: true,
      status: 'ACTIVE',
      settlement: 'T+1',
      recallNotice: 'T+3',
      deliveryType: 'dvp',
      marginType: 'Cash',
      venue: 'OTC',
      dividendType: 'Pass-through',
      collateralType: 'cash',
      governingLaw: 'English Law',
      history: []
    }
  ],
  isActive: true,
  creditRating: 'A',
  country: 'US',
  // LR-389: no MTA threshold by default; overridden per-CP in createMockCounterpartyList.
  minimumTransferAmount: null,
  minimumTransferAmountCurrency: 'USD',
  ...overrides,
});

export const createMockAgreement = (overrides?: Record<string, unknown>): Record<string, unknown> => ({
  id: '1',
  type: 'gmsla',
  status: 'ACTIVE',
  valid_from: '2024-01-01',
  valid_to: '2025-12-31',
  allowed_trade_types: ['lend', 'borrow'],
  default_settlement_venue: 'dtc',
  default_delivery_type: 'dvp',
  default_margin_rate: 105,
  default_dividend_pass_through: 100,
  ...overrides,
});

// Names aligned with mockTradeBlotterData so auto-generated per-CP views
// line up with the trades table. 'JP Morgan' carries the single LR-385 margined
// (105%) repo (id 16) — the example for the repo margin call under `pnpm mock`.
const MOCK_COUNTERPARTY_NAMES = [
  'Barclays',
  'CIMB',
  'Deutsche Bank',
  'Goldman Sachs',
  'JP Morgan',
  'Morgan Stanley',
  'StoneX',
  'UBS',
];

/** The id `createMockCounterpartyList` gives `name`, so a mock trade points at its registry row. */
export const mockCounterpartyId = (name: string): number =>
  MOCK_COUNTERPARTY_NAMES.indexOf(name) + 1;

export const createMockCounterpartyList = (count: number = 8): Counterparty[] => {
  const names = MOCK_COUNTERPARTY_NAMES;
  // LR-389: per-CP minimum transfer amount. Goldman Sachs = 50,000 (Repricing - MtM.xlsx
  // Scenario 2); 'JP Morgan' intentionally omitted to exercise the no-threshold (null) path.
  const mtaByName: Record<string, number> = {
    'Goldman Sachs': 50_000,
    'Morgan Stanley': 25_000,
    'UBS': 100_000,
    'Deutsche Bank': 75_000,
    'Barclays': 0,
    'StoneX': 30_000,
    'CIMB': 10_000,
  };
  const agreementTypes: FrameworkAgreement[] = ['gmsla', 'gmra'];
  return Array.from({ length: count }, (_, index) => {
    const name = names[index % names.length];
    return createMockCounterparty({
      id: index + 1,
      name,
      clientId: `CLIENT-${1000 + index}`,
      minimumTransferAmount: name in mtaByName ? mtaByName[name] : null,
      minimumTransferAmountCurrency: 'USD',
      // JP Morgan books repo in the mock blotter too, so it holds a GMRA agreement beside its
      // GMSLA one, as the agreement migration gives a counterparty for every product it trades.
      agreements: (name === 'JP Morgan'
        ? [agreementTypes[index % agreementTypes.length], 'gmra' as FrameworkAgreement]
        : [agreementTypes[index % agreementTypes.length]]
      ).map((type) => ({
          id: type === 'gmra' && name === 'JP Morgan' ? `agr-${index + 1}-gmra` : `agr-${index + 1}`,
          type,
          currency: 'USD',
          agreementId: `AGR-${1000 + index}`,
          validFrom: '2023-01-01',
          isPerpetual: true,
          status: 'ACTIVE',
          settlement: 'T+1',
          recallNotice: 'T+3',
          deliveryType: 'dvp',
          marginType: 'Cash',
          venue: 'OTC',
          dividendType: 'Pass-through',
          collateralType: 'cash',
          governingLaw: 'English Law',
          history: []
      }))
    });
  });
};

export const createMockInventorySearchResults = (query: string): InventoryItem[] => {
  const securities = ['AAPL.NASDAQ', 'MSFT.NASDAQ', 'GOOGL.NASDAQ', 'AMZN.NASDAQ', 'TSLA.NASDAQ'];
  const isins = ['US0378331005', 'US5949181045', 'US02079K3059', 'US0231351067', 'US88160R1014'];
  const allItems: InventoryItem[] = Array.from({ length: 20 }, (_, index) =>
    createMockInventoryItem({
      symbol_id: securities[index % securities.length],
      isin: isins[index % isins.length],
      short_name: securities[index % securities.length].split('.')[0],
    })
  );
  if (!query) return allItems.slice(0, 10);
  const lowerQuery = query.toLowerCase();
  return allItems
    .filter(
      (item) =>
        item.symbol_id.toLowerCase().includes(lowerQuery) ||
        item.isin.toLowerCase().includes(lowerQuery) ||
        item.short_name.toLowerCase().includes(lowerQuery)
    )
    .slice(0, 10);
};

interface MockTradeInput {
  id: string;
  security: {
    symbol: string;
    shortName: string;
    description: string;
    sedol: string;
    isin: string;
    type: string;
    country: string;
    depository: string;
    currency: string;
  };
  account: {
    legalEntity: string;
    accountId: string;
  };
  counterparty: {
    name: string;
    firmId: string;
    agreement: FrameworkAgreement;
    agreementDate: string;
  };
  position: {
    tradeType: TradeType;
    collateralType: CollateralType;
    quantity: number;
    availableQuantity: number;
    contractPrice: number;
    contractValue: number;
    /** Optional last Mark-to-Market price. Absent → TradeItem.last_marked_price is null (fallback to gross). */
    marketPrice?: number;
    /** LR-494: optional COB (T-1 close) price in the SECURITY currency. Absent →
     *  TradeItem.cob_price is null (COB Price column shows "—"). */
    cobPrice?: number;
  };
  rate: {
    rateType: 'FIXED' | 'FLOATING';
    fixedRate?: number;
    spread?: number;
    benchmark?: BenchmarkRate;
    dividendPassThrough: number;
  };
  dates: {
    tradeDate: string;
    tradeTime: string;
    settlementDate: string;
    maturityDate?: string;
  };
  settlement: {
    deliveryType: DeliveryType;
    venue: SettlementVenue;
    netSettlement: number;
    marginPrice: number;
    /** LR-474: settlement currency when it differs from the security's (cross-ccy
     *  demo trades); defaults to the security currency. */
    currency?: string;
    /** Booked FX rate security → settlement; defaults to 1. */
    fxRate?: number;
    /** LR-494: COB (T-1 close) FX rate security → settlement; null/omitted →
     *  the COB FX column falls back to the booked fxRate. */
    cobFxRate?: number;
  };
  comments: string;
  status: TradeStatus;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  version: number;
}

// Status → display label, mirroring backend Position.STATUS_CHOICES (LR-497) so a
// mock's status_display matches what the real API sends. Kept in lock-step with
// TRADE_STATUS_LABELS for the stored statuses. The old
// `charAt(0).toUpperCase() + slice(1)` produced the 23-char
// "Pending_open_settlement", which overflowed the fixed-width Status column and
// overlapped the Symbol cell.
const MOCK_STATUS_DISPLAY: Record<TradeStatus, string> = {
  pending: 'Pending Trade Date',
  pending_open_settlement: 'Pending Settlement',
  active: 'Active',
  matured: 'Matured',
  closed: 'Closed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};

const convertMockTradeToTradeItem = (trade: MockTradeInput): TradeItem => {
  const calculateRate = (): number | undefined => {
    if (trade.rate.rateType === 'FIXED' && trade.rate.fixedRate !== undefined) {
      return trade.rate.fixedRate;
    }
    if (trade.rate.rateType === 'FLOATING' && trade.rate.spread !== undefined) {
      return trade.rate.spread / 100;
    }
    return undefined;
  };
  // LR-474: settlement fields mirror the backend contract — *_converted twins
  // are derived at the booked fx (cross-ccy only; null same-ccy).
  const settlementCcy = trade.settlement.currency ?? trade.security.currency;
  const fxRate = trade.settlement.fxRate ?? 1;
  const isCross = settlementCcy !== trade.security.currency;
  const toSettlement = (v: number | null): number | null =>
    isCross && v != null ? +(v * fxRate).toFixed(2) : null;
  const dailyEarnings = (() => {
    const rate = calculateRate();
    return (trade.rate?.benchmark && rate !== undefined) ? (rate * trade.position.contractValue / 365) : 0;
  })();
  return {
    id: parseInt(trade.id, 10),
    status: trade.status,
    status_display: MOCK_STATUS_DISPLAY[trade.status],
    symbol_id: trade.security.symbol,
    description: trade.security.description,
    isin: trade.security.isin,
    counterparty: trade.counterparty.name,
    counterpartyId: mockCounterpartyId(trade.counterparty.name),
    agreement: trade.counterparty.agreement,
    agreement_display: trade.counterparty.agreement.toUpperCase(),
    trade_type: trade.position.tradeType,
    trade_type_display: trade.position.tradeType,
    collateral_type: trade.position.collateralType,
    quantity: trade.position.quantity,
    gross_price: trade.position.contractPrice,
    last_marked_price: trade.position.marketPrice ?? null,
    gross_price_raw: String(trade.position.contractPrice),
    // LR-494: COB (T-1 close) price in the security currency (null → "—").
    cob_price: trade.position.cobPrice ?? null,
    cob_price_raw: trade.position.cobPrice != null ? String(trade.position.cobPrice) : null,
    current_market_price: null,
    margin_price: trade.settlement.marginPrice,
    contract_value: trade.position.contractValue,
    // margin must be a PERCENTAGE (e.g. 102 = 102%), not a dollar amount.
    // computeMtmRow treats position.margin as marginTenThousandths = margin * 100.
    // Derive from marginPrice / contractPrice * 100; guard divide-by-zero with 100 (par).
    margin: trade.position.contractPrice > 0
      ? (trade.settlement.marginPrice / trade.position.contractPrice) * 100
      : 100,
    total_rate: (() => {
      const rate = calculateRate();
      return rate !== undefined ? rate : null;
    })(),
    fee_percent: null,
    // Renamed daily_earnings_usd → daily_earnings (origin/main); keep our
    // `dailyEarnings` base so the converted twin below (toSettlement(dailyEarnings))
    // stays consistent with it.
    daily_earnings: dailyEarnings,
    trade_date: trade.dates.tradeDate,
    settlement_date: trade.dates.settlementDate,
    maturity_date: trade.dates.maturityDate || '',
    custodian_name: null,
    currency: trade.security.currency,
    settlement_currency: settlementCcy,
    fx_rate: fxRate,
    // LR-494: COB (T-1 close) FX rate; null → the column falls back to fx_rate.
    cob_fx_rate: trade.settlement.cobFxRate ?? null,
    margin_price_converted: toSettlement(trade.settlement.marginPrice),
    contract_value_converted: toSettlement(trade.position.contractValue),
    daily_earnings_converted: toSettlement(dailyEarnings),
    closing_cash_converted: null,
    created_at: trade.createdAt,
    updated_at: trade.updatedAt,
    created_by: trade.createdBy,
  };
};

// Pre-existing MTM marks per position id. Picked so the "Mark history" micro-button
// (LR-386 ClockIcon) is visible in the MTM table without first having to run MTM.
// Numbers chosen so the per-mark delta (vs the booking gross_price) is sensible:
//   #1 QPMX gross=82.75 → marked at 85.10 (positive Δ)
//   #2 FBTX gross=$50.25 → marked at 48.40 (negative Δ)
const BLOTTER_LAST_MARK: Readonly<Record<number, string>> = {
  1: '85.1000',
  2: '48.4000',
  // LR-387 demo: Goldman Sachs EUR (Siemens, id 14) + GBP (BP, id 15) lends carry a
  // prior mark so the MTM bar shows a NON-ZERO per-currency split on first load
  // (baseline = this mark, effective = gross; no override/COB). EUR gross 175 vs mark
  // 170 → +10,000 EUR; GBP gross 5.00 vs mark 5.40 → −2,000 GBP. Goldman's USD slice
  // comes from the pre-existing FBTX mark (id 2), so all three currency cells render.
  14: '170.0000',
  15: '5.4000',
};

const withInitialMark = (trade: TradeItem): TradeItem => {
  const mark = BLOTTER_LAST_MARK[trade.id];
  if (mark == null) return trade;
  // Set BOTH the *_raw string mirrors and the numeric last_marked_price so the
  // fixture matches what transformPositionToTradeItem produces on a real API
  // round-trip. Direct consumers of mockTradeBlotterData (filter-options.ts,
  // trades-mock-store seed) otherwise see last_marked_price=undefined while
  // last_marked_price_raw is set — code that gates on the numeric field would
  // disagree with code that reads the *_raw mirror.
  return {
    ...trade,
    last_marked_price: parseFloat(mark),
    last_marked_price_raw: mark,
    latest_mark_price_raw: mark,
  };
};

// LR-476/522: mirror the backend's Last Marked (post-MtM) display fields + the
// Trade Gross Value onto the mock blotter so those columns render under
// `pnpm mock`. Faithful to the model methods:
//   marked_margin_price     = mark × margin_decimal        (null until marked)
//   marked_contract_value   = qty × mark × margin_decimal  (null until marked)
//   last_marked_gross_value = qty × mark                   (null until marked)
//   trade_gross_value       = qty × gross_price            (always)
// *_converted at the booked fx_rate for cross-ccy (null same-ccy). LR-522 dropped
// the LR-476 booking fallback, so never-marked rows keep the Last Marked fields
// null. Repos are never repriced via MTM (LR-385): mirror Position._last_marked_price
// and treat a repo's mark as absent, so the whole Last Marked stage stays null even
// if a stray mark exists (withInitialMark skips repos anyway — this is defense in
// depth matching the backend guard). Applied AFTER withInitialMark so
// latest_mark_price_raw is in place.
const round2 = (v: number | null): number | null => (v == null ? null : +v.toFixed(2));

const withMarkedFields = (trade: TradeItem): TradeItem => {
  const isRepo = trade.trade_type === 'repo' || trade.trade_type === 'reverse_repo';
  const rawMark = trade.latest_mark_price_raw != null ? parseFloat(trade.latest_mark_price_raw) : null;
  // Repo guard (Position._last_marked_price): repos never surface an MTM-repriced
  // Last Marked figure. The raw latest_mark_price field is untouched (Last Marked
  // Gross Price still reflects any mark) — only the derived stage is suppressed.
  const mark = !isRepo && rawMark != null && Number.isFinite(rawMark) ? rawMark : null;
  const marginDecimal = trade.margin != null ? trade.margin / 100 : null;

  const markedMarginPrice =
    mark != null && marginDecimal != null ? round2(mark * marginDecimal) : null;
  const markedContractValue =
    mark != null && marginDecimal != null ? round2(trade.quantity * mark * marginDecimal) : null;
  const lastMarkedGrossValue = mark != null ? round2(trade.quantity * mark) : null;
  const tradeGrossValue = trade.gross_price != null ? round2(trade.quantity * trade.gross_price) : null;

  const isCross =
    !!trade.settlement_currency &&
    trade.settlement_currency.toUpperCase() !== (trade.currency || '').toUpperCase();
  const fx = trade.fx_rate ?? null;
  const toConverted = (v: number | null): number | null =>
    isCross && v != null && fx != null ? round2(v * fx) : null;

  return {
    ...trade,
    marked_margin_price: markedMarginPrice,
    marked_contract_value: markedContractValue,
    marked_margin_price_converted: toConverted(markedMarginPrice),
    marked_contract_value_converted: toConverted(markedContractValue),
    last_marked_gross_value: lastMarkedGrossValue,
    last_marked_gross_value_converted: toConverted(lastMarkedGrossValue),
    trade_gross_value: tradeGrossValue,
    trade_gross_value_converted: toConverted(tradeGrossValue),
    // What the BO recall leg takes a share of: the marked value once MTM moved BO there, else the
    // booked cash. A mock approximation (rebate rows are not left at the booking here).
    collateral_to_return:
      trade.collateral_type === 'uncollateralized'
        ? null
        : (isCross ? toConverted(markedContractValue) : markedContractValue) ??
          (isCross ? trade.contract_value_converted ?? null : trade.contract_value),
  };
};

// LR-521: mirror the backend's COB margin price + gross/margin values onto the
// mock blotter so the new COB columns render populated (not "—") under
// `pnpm mock`. Faithful to the serializer: cob_price × margin, × qty, × qty ×
// margin; *_converted at the COB FX rate (fallback booked fx_rate) for cross-ccy.
// Rows without a cob_price keep the COB value columns blank (honest).
// LR-626: mirror `Position.calculate_closing_cash` onto the mock blotter so the
// Closing Cash column and the trade-detail pair render populated under
// `pnpm mock` (they were blank for every row before — the fixture never carried
// the field, so the GMRA-only gate could not be exercised without a backend).
//
// Faithful to the backend on both branches:
//   GMSLA (lend/borrow): last_marked_price × qty × margin_decimal — principal
//     only, no interest. Falls back to the booking snapshot current_market_price
//     before the first mark, like the model property does.
//   GMRA (repo/reverse):  contract_value + rate × contract_value / day_count × days,
//     accruing over [settlement, maturity) — LR-462. Without a maturity_date the
//     figure is projected to MOCK_PROJECTION_DATE and flagged
//     `closing_cash_projected`, which is what renders the "(projected)" tag.
//
// The projection date is a CONSTANT, not `new Date()`: mockTradeBlotterData is
// consumed by tests (filter options, mock-store seed), and a value that moves
// with the wall clock would make them drift.
const MOCK_PROJECTION_DATE = '2026-09-01';

const daysBetween = (from: string, to: string): number =>
  Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000));

/**
 * LR-700: the mock blotter carried no `term_type` at all, so under `pnpm mock` the
 * Term pill had nothing to count (the /positions/metrics/ mock aggregates it now).
 * Assigned deterministically by id — stable across reloads, and all three values
 * present so the pill, its counts and the sidebar's Trade Term section are
 * exercised. Repo/reverse-repo are dated trades in this fixture, so they never draw
 * `evergreen` (a notice-driven term makes no sense for them).
 */
const TERM_TYPES_BY_SLOT: TermType[] = ['open', 'term', 'evergreen'];

const withTermType = (trade: TradeItem): TradeItem => {
  if (trade.term_type) return trade;
  const isRepo = trade.trade_type === 'repo' || trade.trade_type === 'reverse_repo';
  const slot = trade.id % (isRepo ? 2 : TERM_TYPES_BY_SLOT.length);
  return { ...trade, term_type: TERM_TYPES_BY_SLOT[slot] };
};

const withClosingCash = (trade: TradeItem): TradeItem => {
  const isGmra = trade.trade_type === 'repo' || trade.trade_type === 'reverse_repo';
  const marginDecimal = trade.margin != null ? trade.margin / 100 : 1;
  let native: number | null = null;
  let projected = false;

  if (isGmra) {
    const rate = trade.total_rate;
    if (rate != null && trade.contract_value) {
      const start = trade.settlement_date || trade.trade_date;
      const end = trade.maturity_date || MOCK_PROJECTION_DATE;
      projected = !trade.maturity_date;
      const days = start ? daysBetween(start, end) : 0;
      const interest =
        (rate * trade.contract_value) / 100 / dayCountForCurrency(trade.currency) * days;
      native = round2(trade.contract_value + interest);
    }
  } else {
    const price = trade.last_marked_price ?? trade.current_market_price ?? null;
    if (price != null && trade.quantity != null) {
      native = round2(price * trade.quantity * marginDecimal);
      // GMSLA principal has no maturity dependency, so the flag only ever marks
      // the open-term case (Position.closing_cash_projected).
      projected = !trade.maturity_date;
    }
  }

  if (native == null) return trade;
  const isCross =
    !!trade.settlement_currency &&
    trade.settlement_currency.toUpperCase() !== (trade.currency || '').toUpperCase();
  const fx = trade.fx_rate ?? null;
  return {
    ...trade,
    closing_cash: native,
    closing_cash_projected: projected,
    closing_cash_converted: isCross && fx != null ? round2(native * fx) : null,
  };
};

const withCobValueFields = (trade: TradeItem): TradeItem => {
  const cob = trade.cob_price ?? null;
  if (cob == null) return trade;
  const marginDecimal = trade.margin != null ? trade.margin / 100 : 1;
  // Unrounded security-ccy bases. The serializer (_cob_to_settlement) converts
  // the UNROUNDED figure and quantizes ONCE, so native and converted each round
  // the raw product — NOT the already-rounded native (that double-rounds and can
  // drift a cent from the real backend).
  const cobMarginPriceRaw = cob * marginDecimal;
  const cobGrossValueRaw = cob * trade.quantity;
  const cobMarginValueRaw = cob * trade.quantity * marginDecimal;
  const isCross =
    !!trade.settlement_currency &&
    trade.settlement_currency.toUpperCase() !== (trade.currency || '').toUpperCase();
  const cobFx = trade.cob_fx_rate ?? trade.fx_rate ?? null;
  const toConv = (raw: number): number | null =>
    isCross && cobFx != null ? round2(raw * cobFx) : null;
  return {
    ...trade,
    cob_margin_price: round2(cobMarginPriceRaw),
    cob_gross_value: round2(cobGrossValueRaw),
    cob_margin_value: round2(cobMarginValueRaw),
    cob_margin_price_converted: toConv(cobMarginPriceRaw),
    cob_gross_value_converted: toConv(cobGrossValueRaw),
    cob_margin_value_converted: toConv(cobMarginValueRaw),
  };
};

const rawMockTradeBlotterData: TradeItem[] = [
  convertMockTradeToTradeItem({
    id: '1',
    security: {
      symbol: 'QPMX',
      shortName: 'QIAGEN Holdings',
      description: 'QIAGEN Holdings',
      sedol: 'B7KMH81',
      isin: 'US25470M1036',
      type: 'STOCK',
      country: 'US',
      depository: 'DTC',
      currency: 'USD',
    },
    account: {
      legalEntity: 'Malta',
      accountId: 'EXT1111.001',
    },
    counterparty: {
      name: 'StoneX',
      firmId: 'STNX001',
      agreement: 'gmsla',
      agreementDate: '2024-01-15',
    },
    position: {
      tradeType: 'lend',
      collateralType: 'cash',
      quantity: 50000,
      availableQuantity: 273559,
      contractPrice: 82.75,
      contractValue: 4137500,
      marketPrice: 85.50,
      // LR-494: COB (T-1 close) in the security currency (USD) for the demo row.
      cobPrice: 84.10,
    },
    rate: {
      rateType: 'FLOATING',
      spread: 50,
      benchmark: 'sofr',
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-15',
      tradeTime: '10:15:30',
      settlementDate: '2025-09-17',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'dtc',
      netSettlement: 4220250.0,
      marginPrice: 84.405,
      // LR-474: cross-ccy demo — USD security settling in EUR at the booked
      // rate, so the pnpm-mock blotter exercises the settlement-primary cells,
      // pill bucketing and tooltips without a live backend.
      currency: 'EUR',
      fxRate: 0.92,
      // LR-494: COB (T-1 close) FX rate distinct from the booked 0.92, so the
      // pnpm-mock blotter shows a real COB FX value (not the booked fallback).
      cobFxRate: 0.9183,
    },
    comments: 'Stock lending QIAGEN Holdings to StoneX - DVP settlement via DTC',
    status: 'active',
    createdAt: '2025-09-15T10:15:30Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-15T10:15:30Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '2',
    security: {
      symbol: 'FBTX',
      shortName: 'Fidelity Ibm Ins USD ETF',
      description: 'Fidelity IBM Insurance USD ETF',
      sedol: 'B987168',
      isin: 'US5351041036',
      type: 'ETF',
      country: 'US',
      depository: 'DTC',
      currency: 'USD',
    },
    account: {
      legalEntity: 'Cyprus',
      accountId: 'EXT1111.002',
    },
    counterparty: {
      name: 'Goldman Sachs',
      firmId: 'GS001',
      agreement: 'gmsla',
      agreementDate: '2024-02-01',
    },
    position: {
      tradeType: 'borrow',
      collateralType: 'cash',
      quantity: 25000,
      availableQuantity: 960498,
      contractPrice: 100.84,
      contractValue: 2521000,
    },
    rate: {
      rateType: 'FIXED',
      fixedRate: 4.25,
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-16',
      tradeTime: '11:05:45',
      settlementDate: '2025-09-17',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'dtc',
      netSettlement: 2647050.0,
      marginPrice: 105.882,
    },
    comments: 'Borrowing Fidelity IBM ETF from Goldman Sachs - Cash collateral',
    status: 'active',
    createdAt: '2025-09-16T11:05:45Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-16T11:05:45Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '3',
    security: {
      symbol: 'DFIC',
      shortName: 'DIMENSIONAL ETF TR DIMENSIONAL INTERNATIONAL COREQUITY 2 ETF',
      description: 'DIMENSIONAL ETF TR DIMENSIONAL INTERNATIONAL COREQUITY 2 ETF',
      sedol: '25434V7',
      isin: 'US25434V7991',
      type: 'ETF',
      country: 'US',
      depository: 'DTC',
      currency: 'USD',
    },
    account: {
      legalEntity: 'Cyprus',
      accountId: 'EXT1111.003',
    },
    counterparty: {
      name: 'Goldman Sachs',
      firmId: 'GS001',
      agreement: 'gmra',
      agreementDate: '2024-02-01',
    },
    position: {
      tradeType: 'repo',
      collateralType: 'non_cash',
      quantity: 15000,
      availableQuantity: 39200,
      contractPrice: 31.65,
      contractValue: 474750,
      marketPrice: 33.50,
    },
    rate: {
      rateType: 'FIXED',
      fixedRate: 3.75,
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-17',
      tradeTime: '14:30:00',
      settlementDate: '2025-09-18',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'dtc',
      netSettlement: 484245.0,
      marginPrice: 32.283,
      // LR-626: the one cross-ccy GMRA row — a USD repo settling in EUR, so the
      // pnpm-mock book exercises the stacked settlement-primary Closing Cash
      // (settlement figure first, security original as the sub-line) without a
      // backend. Every other GMRA row is same-ccy.
      currency: 'EUR',
      fxRate: 0.92,
    },
    comments: 'Repo transaction with DIMENSIONAL ETF - Standard Repo with cash collateral',
    status: 'active',
    createdAt: '2025-09-17T14:30:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-17T14:30:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '4',
    security: {
      symbol: 'ABTL',
      shortName: 'Agrofresh Growing Systems',
      description: 'Agrofresh Growing Systems',
      sedol: 'BYN7X52',
      isin: 'US89876R3638',
      type: 'STOCK',
      country: 'US',
      depository: 'DTC',
      currency: 'USD',
    },
    account: {
      legalEntity: 'Cyprus',
      accountId: 'EXT1111.004',
    },
    counterparty: {
      name: 'Morgan Stanley',
      firmId: 'MS001',
      agreement: 'gmra',
      agreementDate: '2024-03-10',
    },
    position: {
      tradeType: 'reverse_repo',
      collateralType: 'non_cash',
      quantity: 8000,
      availableQuantity: 10488,
      contractPrice: 3.06,
      contractValue: 24480,
      marketPrice: 2.98,
    },
    rate: {
      rateType: 'FIXED',
      fixedRate: 2.85,
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-18',
      tradeTime: '15:45:00',
      settlementDate: '2025-09-19',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'dtc',
      netSettlement: 25214.4,
      marginPrice: 3.1518,
    },
    comments: 'Reverse Repo with Agrofresh Growing Systems - Standard Reverse Repo with cash collateral',
    status: 'active',
    createdAt: '2025-09-18T15:45:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-18T15:45:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '5',
    security: {
      symbol: 'ABTL',
      shortName: 'FO Nexus',
      description: 'FO Nexus',
      sedol: 'BYR4M22',
      isin: 'US76876X3364',
      type: 'STOCK',
      country: 'US',
      depository: 'DTC',
      currency: 'USD',
    },
    account: {
      legalEntity: 'Malta',
      accountId: 'EXT1111.001',
    },
    counterparty: {
      name: 'UBS',
      firmId: 'UBS001',
      agreement: 'gmsla',
      agreementDate: '2024-04-20',
    },
    position: {
      tradeType: 'lend',
      collateralType: 'non_cash',
      quantity: 9000,
      availableQuantity: 10488,
      contractPrice: 7.22,
      contractValue: 64980,
    },
    rate: {
      rateType: 'FLOATING',
      spread: 45,
      benchmark: 'sofr',
      dividendPassThrough: 85,
    },
    dates: {
      tradeDate: '2025-09-19',
      tradeTime: '09:30:00',
      settlementDate: '2025-09-21',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'dtc',
      netSettlement: 68229.0,
      marginPrice: 7.581,
    },
    comments: 'Stock lending FO Nexus to UBS - DVP settlement via DTC',
    status: 'active',
    createdAt: '2025-09-19T09:30:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-19T09:30:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '6',
    security: {
      symbol: 'QPMX',
      shortName: 'QIAGEN Holdings',
      description: 'QIAGEN Holdings',
      sedol: 'B7KMH81',
      isin: 'US25470M1036',
      type: 'STOCK',
      country: 'US',
      depository: 'DTC',
      currency: 'USD',
    },
    account: {
      legalEntity: 'Malta',
      accountId: 'EXT1111.002',
    },
    counterparty: {
      name: 'Deutsche Bank',
      firmId: 'DB001',
      agreement: 'gmsla',
      agreementDate: '2024-06-01',
    },
    position: {
      tradeType: 'borrow',
      collateralType: 'non_cash',
      quantity: 30000,
      availableQuantity: 273559,
      contractPrice: 82.75,
      contractValue: 2482500,
      marketPrice: 80.10,
    },
    rate: {
      rateType: 'FLOATING',
      spread: 38,
      benchmark: 'sofr',
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-20',
      tradeTime: '13:20:15',
      settlementDate: '2025-09-22',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'dtc',
      netSettlement: 2681100.0,
      marginPrice: 89.37,
    },
    comments: 'Borrowing QIAGEN Holdings from Deutsche Bank - Cash collateral',
    status: 'active',
    createdAt: '2025-09-20T13:20:15Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-20T13:20:15Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '7',
    security: {
      symbol: 'HSBA',
      shortName: 'HSBC Holdings plc',
      description: 'HSBC Holdings plc',
      sedol: '0409410',
      isin: 'GB0005405286',
      type: 'STOCK',
      country: 'GB',
      depository: 'CREST',
      currency: 'GBP',
    },
    account: {
      legalEntity: 'Malta',
      accountId: 'EXT1111.001',
    },
    counterparty: {
      name: 'Barclays',
      firmId: 'BARC001',
      agreement: 'gmsla',
      agreementDate: '2024-05-15',
    },
    position: {
      tradeType: 'lend',
      collateralType: 'cash',
      quantity: 12000,
      availableQuantity: 12000,
      contractPrice: 6.45,
      contractValue: 77400,
      marketPrice: 6.72,
    },
    rate: {
      rateType: 'FLOATING',
      spread: 42,
      benchmark: 'sonia',
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-21',
      tradeTime: '10:30:00',
      settlementDate: '2025-09-23',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'euroclear',
      netSettlement: 81270.0,
      marginPrice: 6.7725,
    },
    comments: 'Stock lending HSBC Holdings in GBP to Barclays - DVP settlement via CREST',
    status: 'active',
    createdAt: '2025-09-21T10:30:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-21T10:30:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '8',
    security: {
      symbol: 'SAP',
      shortName: 'SAP SE',
      description: 'SAP SE',
      sedol: 'B0BLSX3',
      isin: 'DE0007164600',
      type: 'STOCK',
      country: 'DE',
      depository: 'CLEARSTREAM',
      currency: 'EUR',
    },
    account: {
      legalEntity: 'Cyprus',
      accountId: 'EXT1111.003',
    },
    counterparty: {
      name: 'Goldman Sachs',
      firmId: 'GS001',
      agreement: 'gmra',
      agreementDate: '2024-02-01',
    },
    position: {
      tradeType: 'repo',
      collateralType: 'cash',
      quantity: 3000,
      availableQuantity: 3000,
      contractPrice: 145.5,
      contractValue: 436500,
    },
    rate: {
      rateType: 'FIXED',
      fixedRate: 3.5,
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-22',
      tradeTime: '11:45:00',
      settlementDate: '2025-09-24',
      // Term repo: closing cash accrues to maturity, so no "projected" tag.
      maturityDate: '2025-12-24',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'euroclear',
      netSettlement: 445230.0,
      marginPrice: 148.41,
    },
    comments: 'Repo transaction with SAP SE in EUR - Standard Repo with cash collateral',
    status: 'active',
    createdAt: '2025-09-22T11:45:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-22T11:45:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '9',
    security: {
      symbol: '700',
      shortName: 'Tencent Holdings Ltd',
      description: 'Tencent Holdings Limited',
      sedol: 'BQ1GQ17',
      isin: 'KYG875721634',
      type: 'STOCK',
      country: 'HK',
      depository: 'HKSCC',
      currency: 'HKD',
    },
    account: {
      legalEntity: 'Malta',
      accountId: 'EXT1111.002',
    },
    counterparty: {
      name: 'CIMB',
      firmId: 'CIMB001',
      agreement: 'gmsla',
      agreementDate: '2024-08-05',
    },
    position: {
      tradeType: 'borrow',
      collateralType: 'cash',
      quantity: 15000,
      availableQuantity: 15000,
      contractPrice: 320.5,
      contractValue: 4807500,
      marketPrice: 315.20,
    },
    rate: {
      rateType: 'FIXED',
      fixedRate: 3.5,
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-23',
      tradeTime: '14:20:00',
      settlementDate: '2025-09-25',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'dtc',
      netSettlement: 4951725.0,
      marginPrice: 330.115,
    },
    comments: 'Borrowing Tencent Holdings in HKD from CIMB - Cash collateral via HKSCC',
    status: 'active',
    createdAt: '2025-09-23T14:20:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-23T14:20:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '10',
    security: {
      symbol: '7203',
      shortName: 'Toyota Motor Corp',
      description: 'Toyota Motor Corporation',
      sedol: '6378931',
      isin: 'JP3633400001',
      type: 'STOCK',
      country: 'JP',
      depository: 'JASDEC',
      currency: 'JPY',
    },
    account: {
      legalEntity: 'Cyprus',
      accountId: 'EXT1111.004',
    },
    counterparty: {
      name: 'Morgan Stanley',
      firmId: 'MS001',
      agreement: 'gmra',
      agreementDate: '2024-03-10',
    },
    position: {
      tradeType: 'reverse_repo',
      collateralType: 'cash',
      quantity: 10000,
      availableQuantity: 10000,
      contractPrice: 2850.0,
      contractValue: 28500000,
    },
    rate: {
      rateType: 'FLOATING',
      spread: 25,
      benchmark: 'tonar',
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-24',
      tradeTime: '09:15:00',
      settlementDate: '2025-09-26',
      maturityDate: '2026-03-26',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'dtc',
      netSettlement: 30210000.0,
      marginPrice: 3021.0,
    },
    comments: 'Reverse Repo with Toyota Motor in JPY - Standard Reverse Repo with JPY cash collateral',
    status: 'active',
    createdAt: '2025-09-24T09:15:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-24T09:15:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '11',
    security: {
      symbol: 'ASML',
      shortName: 'ASML Holding NV',
      description: 'ASML Holding N.V.',
      sedol: '9396543',
      isin: 'NL0010273215',
      type: 'STOCK',
      country: 'NL',
      depository: 'EUROCLEAR',
      currency: 'EUR',
    },
    account: {
      legalEntity: 'Malta',
      accountId: 'EXT1111.001',
    },
    counterparty: {
      name: 'UBS',
      firmId: 'UBS001',
      agreement: 'gmsla',
      agreementDate: '2024-04-20',
    },
    position: {
      tradeType: 'lend',
      collateralType: 'cash',
      quantity: 2500,
      availableQuantity: 2500,
      contractPrice: 785.5,
      contractValue: 1963750,
      marketPrice: 810.00,
    },
    rate: {
      rateType: 'FLOATING',
      spread: 48,
      benchmark: 'estr',
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-25',
      tradeTime: '13:00:00',
      settlementDate: '2025-09-27',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'euroclear',
      netSettlement: 2042300.0,
      marginPrice: 816.92,
    },
    comments: 'Stock lending ASML Holding in EUR to UBS - DVP settlement via Euroclear',
    status: 'active',
    createdAt: '2025-09-25T13:00:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-25T13:00:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '12',
    security: {
      symbol: 'NVDA',
      shortName: 'NVIDIA Corporation',
      description: 'NVIDIA Corporation',
      sedol: '2379504',
      isin: 'US67066G1040',
      type: 'STOCK',
      country: 'US',
      depository: 'DTC',
      currency: 'USD',
    },
    account: {
      legalEntity: 'Cyprus',
      accountId: 'EXT1111.003',
    },
    counterparty: {
      name: 'Goldman Sachs',
      firmId: 'GS001',
      agreement: 'gmsla',
      agreementDate: '2024-02-01',
    },
    position: {
      tradeType: 'lend',
      collateralType: 'cash',
      quantity: 5000,
      availableQuantity: 5000,
      contractPrice: 875.5,
      contractValue: 4377500,
    },
    rate: {
      rateType: 'FIXED',
      fixedRate: 3.5,
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-26',
      tradeTime: '10:00:00',
      settlementDate: '2025-09-30',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'dtc',
      netSettlement: 4596375.0,
      marginPrice: 919.275,
    },
    comments: 'Stock lending NVIDIA to Goldman Sachs - pending open settlement',
    status: 'pending_open_settlement',
    createdAt: '2025-09-26T10:00:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-26T10:00:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '13',
    security: {
      symbol: 'AMD',
      shortName: 'Advanced Micro Devices',
      description: 'Advanced Micro Devices Inc.',
      sedol: '2007849',
      isin: 'US0079031078',
      type: 'STOCK',
      country: 'US',
      depository: 'DTC',
      currency: 'USD',
    },
    account: {
      legalEntity: 'Cyprus',
      accountId: 'EXT1111.003',
    },
    counterparty: {
      name: 'Goldman Sachs',
      firmId: 'GS001',
      agreement: 'gmsla',
      agreementDate: '2024-02-01',
    },
    position: {
      tradeType: 'borrow',
      collateralType: 'cash',
      quantity: 8000,
      availableQuantity: 8000,
      contractPrice: 145.5,
      contractValue: 1164000,
    },
    rate: {
      rateType: 'FIXED',
      fixedRate: 3.5,
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-27',
      tradeTime: '11:00:00',
      settlementDate: '2025-10-01',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'dtc',
      netSettlement: 1222200.0,
      marginPrice: 152.775,
    },
    comments: 'Borrowing AMD from Goldman Sachs - settled, accruing',
    status: 'active',
    createdAt: '2025-09-27T11:00:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-27T11:00:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  // ── LR-387 demo: multi-currency Stock L/B book under Goldman Sachs (default first
  //    tab). Together with the existing USD FBTX borrow (id 2), these give the MTM
  //    L/B Exposure THREE per-currency readouts at once — USD / EUR / GBP — never
  //    summed. Both are `active` lends so they appear in the MTM view and are marked
  //    via BLOTTER_LAST_MARK so the per-currency exposure is non-zero on first load.
  convertMockTradeToTradeItem({
    id: '14',
    security: {
      symbol: 'SIE',
      shortName: 'Siemens AG',
      description: 'Siemens AG',
      sedol: '5727973',
      isin: 'DE0007236101',
      type: 'STOCK',
      country: 'DE',
      depository: 'CBF',
      currency: 'EUR',
    },
    account: {
      legalEntity: 'Malta',
      accountId: 'EXT1111.014',
    },
    counterparty: {
      name: 'Goldman Sachs',
      firmId: 'GS001',
      agreement: 'gmsla',
      agreementDate: '2024-02-01',
    },
    position: {
      tradeType: 'lend',
      collateralType: 'cash',
      quantity: 2000,
      availableQuantity: 6000,
      contractPrice: 175.0,
      contractValue: 350000,
    },
    rate: {
      rateType: 'FIXED',
      fixedRate: 0.45,
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-24',
      tradeTime: '09:45:00',
      settlementDate: '2025-09-26',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'euroclear',
      netSettlement: 357000.0,
      marginPrice: 175.0,
    },
    comments: 'LR-387: EUR-denominated lend to Goldman Sachs - exercises the per-currency MTM split',
    status: 'active',
    createdAt: '2025-09-24T09:45:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-24T09:45:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  convertMockTradeToTradeItem({
    id: '15',
    security: {
      symbol: 'BP',
      shortName: 'BP plc',
      description: 'BP plc',
      sedol: '0798059',
      isin: 'GB0007980591',
      type: 'STOCK',
      country: 'GB',
      depository: 'CREST',
      currency: 'GBP',
    },
    account: {
      legalEntity: 'Malta',
      accountId: 'EXT1111.015',
    },
    counterparty: {
      name: 'Goldman Sachs',
      firmId: 'GS001',
      agreement: 'gmsla',
      agreementDate: '2024-02-01',
    },
    position: {
      tradeType: 'lend',
      collateralType: 'cash',
      quantity: 5000,
      availableQuantity: 12000,
      contractPrice: 5.0,
      contractValue: 25000,
    },
    rate: {
      rateType: 'FIXED',
      fixedRate: 0.4,
      dividendPassThrough: 100,
    },
    dates: {
      tradeDate: '2025-09-24',
      tradeTime: '10:15:00',
      settlementDate: '2025-09-26',
    },
    settlement: {
      deliveryType: 'dvp',
      venue: 'euroclear',
      netSettlement: 25500.0,
      marginPrice: 5.0,
    },
    comments: 'LR-387: GBP-denominated lend to Goldman Sachs - exercises the per-currency MTM split',
    status: 'active',
    createdAt: '2025-09-24T10:15:00Z',
    createdBy: 'trader@exante.com',
    updatedAt: '2025-09-24T10:15:00Z',
    updatedBy: 'trader@exante.com',
    version: 1,
  }),
  // LR-385: a margined (105%) repo so `pnpm mock` can demo the repo margin call on a non-100%
  // trade. Counterparty 'JP Morgan' — the name createMockCounterpartyList (GET /counterparties/)
  // uses, so it gets an MTM tab (/trades?mode=mtm → JP Morgan → Repo bucket). Both sides carry
  // the margin (customer rule): trade_cash = contract_value = 105,000 (qty × margin_price);
  // market_value = qty × 102 × 1.05 = 107,100 → call +2,100 "Due to us" (the +2% price move,
  // scaled by margin). A freshly-booked one (price 100) would net to 0 — no day-1 buffer call.
  createMockTradeItem({
    id: 16,
    symbol_id: 'MSFT.NASDAQ',
    description: 'Microsoft Corp',
    isin: 'US5949181045',
    counterparty: 'JP Morgan',
    agreement: 'gmra',
    agreement_display: 'GMRA',
    trade_type: 'repo',
    trade_type_display: 'Repo',
    collateral_type: 'non_cash',
    quantity: 1000,
    gross_price: 100,
    gross_price_raw: '100.00',
    cob_price: 100,
    cob_price_raw: '100.00',
    current_market_price: 102,
    current_market_price_updated_at: '2026-07-13T12:00:00Z', // LR-532: demo "as of" tooltip
    margin_price: 105,
    contract_value: 105000,
    margin: 105,
    daily_earnings: 12.5,
    trade_date: '2026-06-01',
    settlement_date: '2026-06-02',
    currency: 'USD',
  }),
];

// LR-497: demo rows that surface the settlement statuses a trade can hold — including the two
// FE-derived buckets and the retry-chip combo — under `pnpm mock`, so the widened
// Status column can be eyeballed. These are exactly the labels that overflowed the
// old 100px column. Existing CP ('StoneX') + USD keep the counterparty/currency
// filter option sets unchanged; only the symbol set gains a few entries.
const lr497StatusDemoRows: TradeItem[] = [
  // pending + prior BO failure → "Pending Trade Date" pill + "retry 1/3" chip
  // (the combination that wrapped and overlapped the row below).
  createMockTradeItem({
    id: 9701,
    status: 'pending',
    status_display: 'Pending Trade Date',
    symbol_id: 'TSLA.NASDAQ',
    description: 'Tesla Inc',
    counterparty: 'StoneX',
    bo_retry_count: 1,
    last_bo_error: 'BO POST failed: symbol not mapped',
  }),
  // LR-712: the TRD-111 shape, so the fix is visible under `pnpm mock` and not
  // only in unit tests — a trade whose CURRENT blocker is a timed-out opening
  // transfer while `last_bo_error` still carries an older, unrelated refusal.
  // The blotter must describe the transfer and never the string; the trade page
  // shows the posting panel. LR-711 shipped `bo_posting` through the transformer
  // but seeded no row carrying one, so every mock row left that path dead.
  createMockTradeItem({
    id: 9705,
    status: 'rejected',
    status_display: 'Rejected',
    symbol_id: 'META.NASDAQ',
    description: 'Meta Platforms Inc',
    counterparty: 'StoneX',
    bo_retry_count: 3,
    last_bo_error: 'BO POST failed. {"description":{"accountId":"account not found"}}',
    bo_posting: {
      state: 'timed_out',
      op: 'transfer_open',
      leg: 'securities',
      attempted_at: '2026-08-15T09:12:00Z',
      is_unresolved_failure: true,
    },
  }),
  // FOP opening, exactly one leg settled → "Partially Settled".
  createMockTradeItem({
    id: 9702,
    status: 'pending_open_settlement',
    status_display: 'Pending Settlement',
    symbol_id: 'NVDA.NASDAQ',
    description: 'NVIDIA Corp',
    counterparty: 'StoneX',
    delivery_type: 'fop',
    cash_settled: true,
    securities_settled: false,
    cash_settled_at: '2026-06-02',
  }),
  // plain opening (no FOP one-leg) → "Pending Settlement".
  createMockTradeItem({
    id: 9703,
    status: 'pending_open_settlement',
    status_display: 'Pending Settlement',
    symbol_id: 'AMZN.NASDAQ',
    description: 'Amazon.com Inc',
    counterparty: 'StoneX',
  }),
  // active + pending partial recall → "Pending Partial Settlement" (longest label).
  createMockTradeItem({
    id: 9704,
    status: 'active',
    status_display: 'Active',
    symbol_id: 'MSFT.NASDAQ',
    description: 'Microsoft Corp',
    counterparty: 'StoneX',
    pending_recall_quantity: 250,
  }),
];

export const mockTradeBlotterData: TradeItem[] = [
  ...rawMockTradeBlotterData
    .map(withInitialMark)
    .map(withMarkedFields)
    .map(withCobValueFields)
    .map(withClosingCash),
  ...lr497StatusDemoRows,
  // LR-700: applied to the WHOLE set, after the demo rows join it — they bypass the
  // pipeline above, and a row without a term is invisible to the Term pill.
].map(withTermType);

// Pre-existing MarkToMarket history per position id — feeds the LR-386 mock store at
// boot time so the "Mark history" drawer shows a multi-entry timeline (with deltas)
// for the same positions that surface a clock icon in the table.
export const BLOTTER_INITIAL_MARKS: Readonly<Record<number, ReadonlyArray<{
  price: string;
  marked_at: string;
  source: 'automatic' | 'manual';
  marked_by: string | null;
  notes: string;
}>>> = {
  1: [
    { price: '85.1000', marked_at: '2025-12-02T16:00:00Z', source: 'manual', marked_by: 'trader@exante.com', notes: 'EOD MTM run' },
    { price: '83.2500', marked_at: '2025-11-28T16:00:00Z', source: 'automatic', marked_by: null, notes: '' },
    { price: '82.7500', marked_at: '2025-11-21T16:00:00Z', source: 'automatic', marked_by: null, notes: '' },
  ],
  2: [
    { price: '48.4000', marked_at: '2025-12-02T16:00:00Z', source: 'manual', marked_by: 'risk@exante.com', notes: 'Counterparty agreed mark' },
    { price: '49.1000', marked_at: '2025-11-25T16:00:00Z', source: 'automatic', marked_by: null, notes: '' },
  ],
};

// Phase 7: all 4 trade_types under one counterparty for computeMtmExposure unit tests.
// qty=1000, BOOKING price=100, margin=105 -> baselineMarginedValue = 1000 * 100 * 1.05 = 105000.
// latest_mark_price_raw is undefined (NEVER MARKED) -> baseline = gross/booking. Exposure is
// the INCREMENTAL margin call: it is 0 until the price actually moves (via an override that the
// user enters, or a prior mark). contract_value mirrors the margined booking (105000) but is no
// longer the baseline source — the moving baseline derives from latest_mark_price_raw ?? gross.
export const createMtmExposureFixtures = (counterparty = 'Goldman Sachs') => ({
  lend:         createMockTradeItem({ id: 101, trade_type: 'lend',         counterparty, status: 'active', quantity: 1000, gross_price: 100, gross_price_raw: '100.00', contract_value: 105000, last_marked_price: null }),
  borrow:       createMockTradeItem({ id: 102, trade_type: 'borrow',       counterparty, status: 'active', quantity: 1000, gross_price: 100, gross_price_raw: '100.00', contract_value: 105000, last_marked_price: null }),
  repo:         createMockTradeItem({ id: 103, trade_type: 'repo',         counterparty, status: 'active', quantity: 1000, gross_price: 100, gross_price_raw: '100.00', contract_value: 105000, last_marked_price: null }),
  reverse_repo: createMockTradeItem({ id: 104, trade_type: 'reverse_repo', counterparty, status: 'active', quantity: 1000, gross_price: 100, gross_price_raw: '100.00', contract_value: 105000, last_marked_price: null }),
});

